/**
 * @fileoverview VideoPlayer Component
 * 
 * A comprehensive video player component for displaying fall incident video clips
 * with custom controls, progress tracking, and incident information display.
 * 
 * @description This component provides:
 * - HTML5 video playback with custom styled controls
 * - Play/pause functionality with visual feedback
 * - Seekable progress bar with click-to-seek capability
 * - Time display (current time / total duration)
 * - Fall incident information panel (location, severity, confidence score)
 * - Responsive layout with video on left, info panel on right
 * - Smooth animations and hover effects
 * 
 * @exports VideoPlayer - Main video player component
 * 
 * @requires react - For component state, refs, and lifecycle management
 * 
 * @example
 * // With fall incident information
 * <VideoPlayer
 *   videoUrl="https://example.com/fall-incident-123.mp4"
 *   fallInfo={{
 *     location_detail: "Room 101 - Bathroom",
 *     severity: "MODERATE",
 *     confidence_score: 0.95
 *   }}
 *   onClose={() => setShowVideo(false)}
 * />
 * 
 * @example
 * // Without fall information (placeholder state)
 * <VideoPlayer
 *   videoUrl="https://example.com/video.mp4"
 *   fallInfo={null}
 *   onClose={() => setShowVideo(false)}
 * />
 */

import { useEffect, useRef, useState } from 'react';

/* ═══════════════════════ SECURITY NOTE ═══════════════════════ */
/**
 * This component uses CloudFront + Lambda@Edge for video authorization.
 * Videos are served directly from CloudFront with cookie-based authentication.
 * 
 * Security Model:
 * - Video URL: https://video.ait.care/FAC-XXXX/video.mp4 (visible but protected)
 * - Browser sends id_token cookie automatically with video requests
 * - Lambda@Edge validates JWT token on EVERY request (including video chunks)
 * - Authorization checks: token signature, expiration, facility_id match
 * - URLs cannot be shared (require valid cookie from same domain)
 */

/* ═══════════════════════ CONSTANTS ═══════════════════════ */

/** @constant {string} W - White color constant used throughout the component */
const W = "#ffffff";

/* ═══════════════════════ MAIN COMPONENT ═══════════════════════ */

/**
 * VideoPlayer - Custom video player with fall incident information display
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.videoUrl - URL of the video file to play
 * @param {Object|null} props.fallInfo - Fall incident information object
 * @param {string} [props.fallInfo.location_detail] - Detailed location of the fall incident
 * @param {string} [props.fallInfo.severity] - Severity level (e.g., "LOW", "MODERATE", "HIGH")
 * @param {number} [props.fallInfo.confidence_score] - AI confidence score (0-1)
 * @param {Function} props.onClose - Callback function to close the video player
 * 
 * @returns {JSX.Element} Rendered video player with controls and incident information
 * 
 * @description
 * Displays a video player with:
 * - Custom controls overlay (play/pause, progress bar, time display)
 * - Seekable progress bar that updates in real-time
 * - Fall incident information panel (when fallInfo is provided)
 * - Placeholder message when no fall info is available
 * - Responsive layout with 55% video, 45% info panel
 * 
 * The component manages video playback state internally and provides
 * visual feedback for all user interactions.
 */
export default function VideoPlayer({ videoUrl, fallInfo, onClose }) {
  /* ═══════════════════════ REFS ═══════════════════════ */
  
  /** @ref {HTMLVideoElement} videoRef - Reference to the video DOM element */
  const videoRef = useRef(null);
  
  /* ═══════════════════════ STATE MANAGEMENT ═══════════════════════ */
  
  /** @state {boolean} isPlaying - Whether the video is currently playing */
  const [isPlaying, setIsPlaying] = useState(false);
  
  /** @state {number} currentTime - Current playback time in seconds */
  const [currentTime, setCurrentTime] = useState(0);
  
  /** @state {number} duration - Total video duration in seconds */
  const [duration, setDuration] = useState(0);
  
  /** @state {number} progress - Playback progress as percentage (0-100) */
  const [progress, setProgress] = useState(0);
  
  /** @state {string|null} videoError - Video loading error message */
  const [videoError, setVideoError] = useState(null);

  /* ═══════════════════════ LIFECYCLE EFFECTS ═══════════════════════ */
  
  /**
   * Set up video event listeners for time tracking and duration
   * 
   * @effect
   * @listens timeupdate - Updates current time and progress percentage
   * @listens loadedmetadata - Sets the video duration once metadata is loaded
   * 
   * @cleanup Removes event listeners when component unmounts or videoUrl changes
   */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Reset error state when video URL changes
    setVideoError(null);

    /**
     * Update current time and progress percentage
     * Called on every timeupdate event during playback
     */
    const updateTime = () => {
      setCurrentTime(video.currentTime);
      // Calculate progress as percentage, handle division by zero
      setProgress((video.currentTime / video.duration) * 100 || 0);
    };

    /**
     * Update video duration once metadata is loaded
     * Called when video metadata becomes available
     */
    const updateDuration = () => {
      setDuration(video.duration);
      console.log('[VideoPlayer] Video metadata loaded, duration:', video.duration);
    };

    /**
     * Handle video loading errors
     * Called when video fails to load
     */
    const handleError = (e) => {
      console.error('[VideoPlayer] Video error:', e);
      console.error('[VideoPlayer] Video error details:', {
        error: video.error,
        networkState: video.networkState,
        readyState: video.readyState,
        src: video.src
      });
      
      let errorMessage = 'Failed to load video';
      if (video.error) {
        switch (video.error.code) {
          case 1: // MEDIA_ERR_ABORTED
            errorMessage = 'Video loading aborted';
            break;
          case 2: // MEDIA_ERR_NETWORK
            errorMessage = 'Network error while loading video';
            break;
          case 3: // MEDIA_ERR_DECODE
            errorMessage = 'Video decoding error';
            break;
          case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED
            errorMessage = 'Video format not supported or unauthorized';
            break;
          default:
            errorMessage = `Video error (code: ${video.error.code})`;
        }
      }
      setVideoError(errorMessage);
    };

    /**
     * Handle successful video load
     */
    const handleLoadStart = () => {
      console.log('[VideoPlayer] Video load started:', videoUrl);
    };

    const handleCanPlay = () => {
      console.log('[VideoPlayer] Video can play');
    };

    // Attach event listeners
    video.addEventListener('timeupdate', updateTime);
    video.addEventListener('loadedmetadata', updateDuration);
    video.addEventListener('error', handleError);
    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('canplay', handleCanPlay);

    // Cleanup function to remove listeners
    return () => {
      video.removeEventListener('timeupdate', updateTime);
      video.removeEventListener('loadedmetadata', updateDuration);
      video.removeEventListener('error', handleError);
      video.removeEventListener('loadstart', handleLoadStart);
      video.removeEventListener('canplay', handleCanPlay);
    };
  }, [videoUrl]); // Re-run effect when video URL changes

  /* ═══════════════════════ EVENT HANDLERS ═══════════════════════ */
  
  /**
   * Toggle video playback between play and pause states
   * 
   * @function togglePlay
   * @returns {void}
   * 
   * @description
   * Toggles the video between playing and paused states.
   * Updates the isPlaying state to reflect the current playback status.
   */
  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    // Toggle between play and pause
    if (isPlaying) {
      video.pause();
    } else {
      video.play();
    }
    setIsPlaying(!isPlaying);
  };

  /**
   * Handle seeking to a specific time in the video
   * 
   * @function handleSeek
   * @param {React.MouseEvent} e - Mouse click event on the progress bar
   * @returns {void}
   * 
   * @description
   * Calculates the clicked position on the progress bar and seeks
   * the video to the corresponding time. The position is calculated
   * as a percentage of the progress bar width.
   */
  const handleSeek = (e) => {
    const video = videoRef.current;
    if (!video) return;

    // Calculate click position as percentage of progress bar width
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    
    // Seek to the corresponding time in the video
    video.currentTime = pos * video.duration;
  };

  /* ═══════════════════════ HELPER FUNCTIONS ═══════════════════════ */
  
  /**
   * Format seconds into MM:SS time format
   * 
   * @function formatTime
   * @param {number} seconds - Time in seconds to format
   * @returns {string} Formatted time string (e.g., "03:45", "00:00")
   * 
   * @description
   * Converts a time value in seconds to a human-readable MM:SS format.
   * Handles invalid inputs (NaN) by returning "00:00".
   * Pads single-digit values with leading zeros.
   * 
   * @example
   * formatTime(125) // Returns "02:05"
   * formatTime(45)  // Returns "00:45"
   * formatTime(NaN) // Returns "00:00"
   */
  const formatTime = (seconds) => {
    // Handle invalid input
    if (isNaN(seconds)) return '00:00';
    
    // Calculate minutes and seconds
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    
    // Format with leading zeros
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  /* ═══════════════════════ RENDER ═══════════════════════ */
  
  return (
    <div style={{ display: "flex", gap: 16, flex: 1, minHeight: 0 }}>
      
      {/* ═══ VIDEO PLAYER SECTION (LEFT SIDE - 55%) ═══ */}
      <div
        style={{
          background: "rgba(0, 0, 0, 0.9)",
          borderRadius: 12,
          flex: "0 0 55%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* HTML5 Video Element - Direct CloudFront URL with Lambda@Edge authorization */}
        {videoUrl && !videoError && (
          <video
            ref={videoRef}
            src={videoUrl}
            crossOrigin="use-credentials"  // Send cookies with video requests to CloudFront
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain", // Maintain aspect ratio
            }}
            onPlay={() => setIsPlaying(true)}   // Sync state when video plays
            onPause={() => setIsPlaying(false)} // Sync state when video pauses
            onContextMenu={(e) => e.preventDefault()} // Disable right-click menu
            controlsList="nodownload" // Disable download button in controls
            disablePictureInPicture // Disable picture-in-picture
          />
        )}

        {/* Video Error Display */}
        {videoError && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: 20,
              textAlign: "center",
            }}
          >
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ff6b6b" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <p style={{ fontSize: 16, margin: "16px 0 8px 0", color: "#ff6b6b", fontWeight: 600 }}>
              {videoError}
            </p>
            <p style={{ fontSize: 13, margin: 0, opacity: 0.7 }}>
              Please check your connection and try again
            </p>
            <button
              onClick={() => {
                setVideoError(null);
                // Force video reload
                if (videoRef.current) {
                  videoRef.current.load();
                }
              }}
              style={{
                marginTop: 16,
                padding: "8px 16px",
                background: "rgba(255, 107, 107, 0.2)",
                border: "2px solid rgba(255, 107, 107, 0.5)",
                borderRadius: 8,
                color: "#ff6b6b",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255, 107, 107, 0.3)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255, 107, 107, 0.2)";
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* ─── Video Controls Overlay ─── */}
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            background: "linear-gradient(to top, rgba(0,0,0,0.8), transparent)",
            padding: "20px 16px 12px",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          {/* Progress Bar - Clickable to seek */}
          <div
            onClick={handleSeek}
            style={{
              width: "100%",
              height: 6,
              background: "rgba(255, 255, 255, 0.3)",
              borderRadius: 3,
              cursor: "pointer",
              position: "relative",
            }}
          >
            {/* Progress indicator (filled portion) */}
            <div
              style={{
                width: `${progress}%`,
                height: "100%",
                background: "linear-gradient(90deg, #ff6b6b 0%, #ee5a6f 100%)",
                borderRadius: 3,
              }}
            />
          </div>

          {/* ─── Play Button and Time Display ─── */}
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {/* Play/Pause Button */}
            <button
              onClick={togglePlay}
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.2)",
                border: "2px solid rgba(255, 255, 255, 0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                // Lighten background on hover
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)";
              }}
              onMouseLeave={(e) => {
                // Reset background
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
              }}
            >
              {/* Conditional icon: Pause when playing, Play when paused */}
              {isPlaying ? (
                // Pause icon (two vertical bars)
                <svg width="16" height="16" viewBox="0 0 24 24" fill={W}>
                  <rect x="6" y="4" width="4" height="16" rx="1" />
                  <rect x="14" y="4" width="4" height="16" rx="1" />
                </svg>
              ) : (
                // Play icon (triangle)
                <svg width="16" height="16" viewBox="0 0 24 24" fill={W}>
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>

            {/* Time Display: Current / Total */}
            <div style={{ fontSize: 13, color: W, fontWeight: 500 }}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
          </div>
        </div>
      </div>

      {/* ═══ VIDEO INFO PANEL SECTION (RIGHT SIDE - 45%) ═══ */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10, flex: 1, minHeight: 0, overflowY: "auto" }}>
        {/* Conditional rendering: Show fall info if available, otherwise show placeholder */}
        {fallInfo ? (
          /* ─── Fall Incident Information Card ─── */
          <div
            style={{
              padding: "16px",
              background: "rgba(74, 144, 226, 0.2)",
              border: "2px solid rgba(74, 144, 226, 0.5)",
              borderRadius: 12,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {/* Card Title */}
            <div style={{ fontWeight: 700, fontSize: 15 }}>Fall Incident Video Clip</div>
            
            {/* Incident Details */}
            <div style={{ fontSize: 13, opacity: 0.9 }}>
              <div>📹 Duration: {formatTime(duration)}</div>
              <div>📍 Location: {fallInfo.location_detail || 'N/A'}</div>
              <div>⚠️ Severity: {fallInfo.severity}</div>
              <div>
                {/* Convert confidence score from 0-1 to percentage */}
                🎯 Confidence: {fallInfo.confidence_score ? `${(fallInfo.confidence_score * 100).toFixed(0)}%` : 'N/A'}
              </div>
            </div>
            
            {/* Info Footer */}
            <div
              style={{
                marginTop: 8,
                padding: 8,
                background: "rgba(0, 0, 0, 0.3)",
                borderRadius: 6,
                fontSize: 12,
                opacity: 0.8,
              }}
            >
              💡 Video clip captured by AI fall detection system
            </div>
          </div>
        ) : (
          /* ─── Placeholder State (No Fall Info) ─── */
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "40px 20px",
              opacity: 0.6,
              flex: 1,
            }}
          >
            {/* Film reel icon */}
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={W} strokeWidth="1.5">
              <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
              <line x1="7" y1="2" x2="7" y2="22" />
              <line x1="17" y1="2" x2="17" y2="22" />
              <line x1="2" y1="12" x2="22" y2="12" />
            </svg>
            {/* Placeholder message */}
            <p style={{ fontSize: 14, margin: "12px 0 0 0", textAlign: "center" }}>
              Click on a fall incident to view video (if available)
            </p>
          </div>
        )}
      </div>


    </div>
  );
}
